import { useMemo } from "react";
import { DataTable, Page } from "@iq/models";

const CHANGES_TABLE_CONFIG = {
    columns: [
        {
            key: "changeId",
            label: "Change #",
            accessor: "changeId",
            filterable: false,
            width: ".75fr",
        },
        {
            key: "title",
            label: "Title",
            accessor: "title",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1.5fr",
        },
        {
            key: "status",
            label: "Status",
            accessor: "status",
            filterable: true,
            quickValues: [
                "New",
                "Assessment",
                "Approved",
                "Scheduled",
                "Implementing",
                "Completed",
                "Failed",
            ],
            width: "1fr",
        },
        {
            key: "priority",
            label: "Priority",
            accessor: "priority",
            filterable: true,
            quickValues: ["Low", "Medium", "High", "Critical"],
            width: ".75fr",
        },
        {
            key: "risk",
            label: "Risk",
            accessor: "risk",
            filterable: true,
            quickValues: ["Low", "Medium", "High"],
            width: ".75fr",
        },
        {
            key: "approvalStatus",
            label: "Approval",
            accessor: "approvalStatus",
            filterable: true,
            quickValues: ["Approved", "Pending", "Rejected"],
            width: "1fr",
        },
        {
            key: "assignedTo",
            label: "Assigned To",
            accessor: "assignedTo",
            filterable: true,
            quickValues: ["A–E", "F–J", "K–O", "P–T", "U–Z"],
            width: "1fr",
        },
        {
            key: "plannedStartAt",
            label: "Planned Start",
            accessor: "plannedStartAt",
            filterable: true,
            quickValues: ["Most Recent", "Oldest"],
        },
    ],
};

function formatTimestamp(value) {
    if (!value) {
        return "N/A";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return parsed.toLocaleString();
}

function Changes({
    changes = [],
    teamMembers = [],
    loadingChanges = false,
    changesError = "",
    loadingTeamMembers = false,
    teamMembersError = "",
    onChangesRefresh,
    onTeamMembersRefresh,
    onChangeSelect,
}) {
    const headerActions = [
        // {
        //  id: "refresh-changes",
        //  label: loadingChanges ? "Refreshing..." : "Refresh",
        //  onClick: onChangesRefresh,
        //  disabled: loadingChanges,
        // },
    ];

    const teamMemberMap = useMemo(
        () =>
            new Map(
                (Array.isArray(teamMembers) ? teamMembers : []).map((member) => [
                    String(member?.id ?? ""),
                    member?.name || member?.email || `User #${member?.id ?? "?"}`,
                ])
            ),
        [teamMembers]
    );

    const tableRows = useMemo(
        () =>
            (Array.isArray(changes) ? changes : []).map((change) => ({
                id: change?.id,
                changeId: change?.number,
                title: change?.title || "Untitled Change",
                status: change?.status || "new",
                priority: change?.priority || "medium",
                risk: change?.risk || "-",
                approvalStatus: change?.approval_status || "-",
                assignedTo:
                    change?.assigned_to_id != null
                        ? teamMemberMap.get(String(change.assigned_to_id)) || `User #${change.assigned_to_id}`
                        : "Unassigned",
                requestedBy:
                    change?.requested_by_id != null
                        ? teamMemberMap.get(String(change.requested_by_id)) || `User #${change.requested_by_id}`
                        : "Unassigned",
                plannedStartAt: formatTimestamp(change?.planned_start_at),
                plannedEndAt: formatTimestamp(change?.planned_end_at),
                __raw: change,
            })),
        [changes, teamMemberMap]
    );

    return (
        <Page>
            {changesError ? (
                <div className="contacts-table-container" style={{ color: "var(--color-danger)", paddingTop: "0.5rem" }}>
                    Failed to load changes: {changesError}
                </div>
            ) : null}
            {teamMembersError ? (
                <div className="contacts-table-container" style={{ color: "var(--color-danger)", paddingTop: "0.5rem" }}>
                    Failed to load team members: {teamMembersError}
                </div>
            ) : null}
            <DataTable
                rowData={tableRows}
                loading={loadingChanges || loadingTeamMembers}
                tableConfig={CHANGES_TABLE_CONFIG}
                headerActions={headerActions}
                onRowClick={(change) => onChangeSelect?.(change)}
            />
        </Page>
    );
}

export default Changes;